import React from 'react';
import { MapPin } from 'lucide-react';
import { visibleLocations } from '../data/mock';
import { useDisabledLocationCodes } from '../data/liveLocations';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';

const ROUTES = {
  'Netherlands': '/vps-netherlands', 'Нидерланды': '/vps-netherlands',
  'Germany': '/vps-germany', 'Германия': '/vps-germany',
  'Poland': '/vps-poland', 'Польша': '/vps-poland',
  'Latvia': '/vps-latvia', 'Латвия': '/vps-latvia',
  'Czech Republic': '/vps-czechia', 'Чехия': '/vps-czechia',
  'Italy': '/vps-italy', 'Италия': '/vps-italy',
  'Switzerland': '/vps-switzerland', 'Швейцария': '/vps-switzerland',
  'USA': '/vps-miami', 'США': '/vps-miami',
  'Bulgaria': '/vps-bulgaria', 'Болгария': '/vps-bulgaria',
  'Greece': '/vps-greece', 'Греция': '/vps-greece',
  'Spain': '/vps-spain', 'Испания': '/vps-spain',
  'Romania': '/vps-romania', 'Румыния': '/vps-romania',
  'Ukraine': '/vps-ukraine', 'Украина': '/vps-ukraine',
};
const DESC = {
  '/vps-netherlands': { en: 'Western Europe hub — lowest latency to the EU & UK.', ru: 'Хаб Западной Европы — минимальный пинг до ЕС и UK.' },
  '/vps-germany': { en: 'Western Germany — premium connectivity across all of Europe.', ru: 'Запад Германии — отличная связность по всей Европе.' },
  '/vps-poland': { en: 'Northern Poland — great fit for Central & Eastern Europe.', ru: 'Север Польши — отлично для Центральной и Восточной Европы.' },
  '/vps-latvia': { en: 'Baltic gateway — clean routing to EU and CIS.', ru: 'Балтийский шлюз — чистые маршруты в ЕС и СНГ.' },
  '/vps-czechia': { en: 'Heart of Central Europe — low latency across the continent.', ru: 'Центр Европы — низкая задержка по всему континенту.' },
  '/vps-italy': { en: 'Southern Europe — strong reach to the Mediterranean.', ru: 'Южная Европа — хорошее покрытие Средиземноморья.' },
  '/vps-switzerland': { en: 'Privacy-first jurisdiction with strict data protection.', ru: 'Приватная юрисдикция со строгой защитой данных.' },
  '/vps-miami': { en: 'US East gateway to North & South America.', ru: 'Восток США — шлюз в Северную и Южную Америку.' },
  '/vps-bulgaria': { en: 'Southeast Europe — solid coverage of the Balkans.', ru: 'Юго-Восток Европы — уверенное покрытие Балкан.' },
  '/vps-greece': { en: 'Mediterranean hub — low ping across SE Europe.', ru: 'Средиземноморский хаб — низкий пинг по ЮВ Европе.' },
  '/vps-spain': { en: 'Iberian peninsula — gateway to Southwest Europe.', ru: 'Пиренейский п-ов — шлюз в Юго-Западную Европу.' },
  '/vps-romania': { en: 'Southeast Europe — low latency to the Balkans.', ru: 'Юго-Восток Европы — низкий пинг к Балканам.' },
  '/vps-ukraine': { en: 'Kyiv, capital of Ukraine — local routing & lowest in-country ping.', ru: 'Киев, столица Украины — локальный трафик и минимальный пинг по стране.' },
};

export const LocationsBar = () => {
  const { t, language } = useLanguage();
  const ru = language === 'ru';
  const disabledCodes = useDisabledLocationCodes(); // выключенные датацентры из админки
  const cur = visibleLocations(language, disabledCodes);
  const lp = (p) => (ru ? '/ru' + p : p);

  return (
    <section id="locations" className="py-20 lg:py-28 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="block text-xs font-bold tracking-widest mb-3" style={{ color: '#1d4ed8' }}>{ru ? 'ГЛОБАЛЬНАЯ СЕТЬ' : 'GLOBAL NETWORK'}</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight">{t('locations.title')}</h2>
          <p className="text-lg text-slate-500 max-w-xl mx-auto">{t('locations.subtitle')}</p>
        </div>

        <div className="loc-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 1, background: '#e8edf3', border: '1px solid #e8edf3', borderRadius: 16, overflow: 'hidden' }}>
          {cur.map((loc, i) => {
            const route = ROUTES[loc.country];
            const d = route && DESC[route] ? (ru ? DESC[route].ru : DESC[route].en) : '';
            const cell = (
              <div className="loc-cell" style={{ background: '#fff', padding: '20px 22px', height: '100%' }}>
                <div className="flex items-center gap-3 mb-3">
                  <img src={loc.flag} alt="" loading="lazy" className="w-9 h-6 object-cover rounded ring-1 ring-slate-200/60" style={{ flex: 'none' }} />
                  <div>
                    <div className="font-semibold text-slate-900 leading-tight">{loc.country}</div>
                    <div className="flex items-center gap-1 text-slate-500 text-xs"><MapPin className="w-3 h-3" />{loc.city}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-500 leading-relaxed" style={{ margin: '0 0 10px' }}>{d}</p>
                
              </div>
            );
            if (loc.comingSoon || !route) return (<div key={i} style={{ opacity: 0.6 }}>{cell}</div>);
            return (<Link key={i} to={lp(route)} className="loc-link" style={{ display: 'block' }}>{cell}</Link>);
          })}
        </div>
      </div>
      <style>{`.loc-cell{transition:background .15s;cursor:pointer}.loc-link:hover .loc-cell{background:#f8fafc!important}@media(max-width:900px){.loc-grid{grid-template-columns:repeat(2,1fr)!important}}@media(max-width:560px){.loc-grid{grid-template-columns:1fr!important}}`}</style>
    </section>
  );
};
